import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslate } from "@tolgee/react";
import { useAuthService } from "../../Context/AuthContext";
import VoiceCloneRecorder from "./VoiceCloneRecorder";
import {
  activateVoiceClone,
  createVoiceClone,
  deleteVoiceClone,
  getVoiceClone,
  speakWithClonedVoice,
  previewDefaultVoice,
} from "../../API/voice/voiceCloningService";
import LocalStorage from "../../Helpers/LocalStorage";
import { useNotify } from "../../Helpers/Notifications";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";

const STATE = {
  RECORD: "record",
  UPLOADING: "uploading",
  PROCESSING: "processing",
  PREVIEW: "preview",
  ACTIVE: "active",
  FAILED: "failed",
};

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 40;

const VoiceCloningSettings = ({ onProceed, onSetupLater: onSetupLaterProp, locale, showHeader = true }) => {
  const { t } = useTranslate();
  const { user, updateUser, getCurrentUser } = useAuthService();
  const { notifyError } = useNotify();

  const effectiveLocale = locale || user?.call_language;

  const savedId = LocalStorage.getVoiceCloneId();

  const [state, setState] = useState(savedId ? STATE.ACTIVE : STATE.RECORD);
  const [voiceId, setVoiceId] = useState(savedId);

  useEffect(() => {
    getCurrentUser(
      (response) => {
        const activeCloneId = response?.data?.user?.active_voice_clone_id;
        if (activeCloneId && !LocalStorage.getVoiceCloneId()) {
          LocalStorage.setVoiceCloneId(activeCloneId);
          setVoiceId(activeCloneId);
          setState(STATE.ACTIVE);
        } else if (!activeCloneId && LocalStorage.getVoiceCloneId()) {
          LocalStorage.removeVoiceCloneId();
          setVoiceId(null);
          setStartWithDefaultVoice(true);
          setRecorderKey((k) => k + 1);
          setState(STATE.RECORD);
        }
      },
      () => {}
    );
  }, []);

  const onSetupLater = onSetupLaterProp
    ? () => {
        if (!user?.gender) {
          updateUser({ gender: "non-binary" }, onSetupLaterProp, onSetupLaterProp);
        } else {
          onSetupLaterProp();
        }
      }
    : undefined;
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [myVoicePhase, setMyVoicePhase] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [recorderKey, setRecorderKey] = useState(0);
  const [startWithDefaultVoice, setStartWithDefaultVoice] = useState(!savedId && !!user?.gender);
  const [consentChecked, setConsentChecked] = useState(false);

  const audioRef = useRef(null);
  const pollTimerRef = useRef(null);
  const pollCountRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const pollVoiceClone = useCallback(
    (id) => {
      pollCountRef.current += 1;
      if (pollCountRef.current > MAX_POLL_ATTEMPTS) {
        setState(STATE.FAILED);
        return;
      }

      getVoiceClone(
        id,
        (response) => {
          const status = response.data?.status;
          if (status === "completed") {
            stopPolling();
            speakWithClonedVoice(
              t("voice_cloning.sample_sentence"),
              id,
              (response) => {
                const url = URL.createObjectURL(response.data);
                setAudioUrl(url);
                setMyVoicePhase("preview");
              },
              () => {
                setMyVoicePhase("preview");
              }
            );
          } else if (status === "failed" || status === "cancelled") {
            stopPolling();
            setMyVoicePhase("failed");
          } else {
            pollTimerRef.current = setTimeout(() => pollVoiceClone(id), POLL_INTERVAL_MS);
          }
        },
        () => {
          pollTimerRef.current = setTimeout(() => pollVoiceClone(id), POLL_INTERVAL_MS);
        }
      );
    },
    [t, stopPolling, onProceed]
  );

  const handleRecordingComplete = useCallback(
    (audioBlob, gender) => {
      if (!audioBlob) {
        updateUser(
          { gender },
          () => { if (onProceed) onProceed(); },
          () => { if (onProceed) onProceed(); }
        );
        return;
      }

      setMyVoicePhase("uploading");
      createVoiceClone(
        audioBlob,
        effectiveLocale,
        (response) => {
          const id = response.data?.voice_id;
          setVoiceId(id);
          pollCountRef.current = 0;
          setMyVoicePhase("processing");
          pollTimerRef.current = setTimeout(() => pollVoiceClone(id), POLL_INTERVAL_MS);
        },
        () => {
          setMyVoicePhase("failed");
          notifyError(t("general.something_wrong"));
        }
      );
    },
    [effectiveLocale, pollVoiceClone, notifyError, t, onProceed]
  );

  const handleSoundsGood = () => {
    activateVoiceClone(
      voiceId,
      () => {
        LocalStorage.setVoiceCloneId(voiceId);
        if (onProceed) {
          onProceed();
        } else {
          setState(STATE.ACTIVE);
        }
      },
      () => {
        LocalStorage.setVoiceCloneId(voiceId);
        if (onProceed) {
          onProceed();
        } else {
          setState(STATE.ACTIVE);
        }
      }
    );
  };

  const handleReRecord = () => {
    stopPolling();
    setIsPlaying(false);
    if (audioRef.current) audioRef.current.pause();
    setAudioUrl(null);
    setMyVoicePhase(null);
    setRecorderKey((k) => k + 1);
    setState(STATE.RECORD);
  };

  const handleDelete = () => {
    if (!voiceId) return;
    const onDone = () => {
      LocalStorage.removeVoiceCloneId();
      setVoiceId(null);
      setStartWithDefaultVoice(true);
      setRecorderKey((k) => k + 1);
      setState(STATE.RECORD);
    };
    deleteVoiceClone(voiceId, onDone, onDone);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    }
  };

  const handlePreviewGender = (gender, onReady, onFail) => {
    const originalGender = user?.gender;
    const restore = () => {
      if (originalGender && originalGender !== gender) {
        updateUser({ gender: originalGender }, () => {}, () => {});
      }
    };

    updateUser(
      { gender },
      () => {
        previewDefaultVoice(
          t("voice_cloning.sample_sentence"),
          effectiveLocale,
          (response) => { restore(); onReady(response.data); },
          () => { restore(); onFail(); }
        );
      },
      onFail
    );
  };

  const renderContent = () => {
    if (state === STATE.RECORD) {
      return (
        <Container>
          {audioUrl && (
            <audio ref={audioRef} src={audioUrl} onEnded={() => setIsPlaying(false)} />
          )}
          <VoiceCloneRecorder
            key={recorderKey}
            onComplete={handleRecordingComplete}
            onSetupLater={onSetupLater}
            defaultGender={user?.gender}
            onPreviewGender={handlePreviewGender}
            startWithDefaultVoice={startWithDefaultVoice}
            isSaveable={!onProceed}
            myVoicePhase={myVoicePhase}
            isPreviewPlaying={isPlaying}
            onTogglePreviewPlay={togglePlay}
            onSoundsGood={handleSoundsGood}
            onReRecord={handleReRecord}
            initialConsent={consentChecked}
            onConsentChange={setConsentChecked}
          />
        </Container>
      );
    }

    return (
      <>
        <Container centered>
          <ActiveOptionCards>
            <ActiveOptionCard selected onClick={() => {}}>
              <ActiveOptionIconWrap><ActiveOptionEmoji>🎙️</ActiveOptionEmoji></ActiveOptionIconWrap>
              <ActiveOptionLabel selected>{t("voice_cloning.my_voice")}</ActiveOptionLabel>
              <ActiveBadge>{t("voice_cloning.active")}</ActiveBadge>
            </ActiveOptionCard>
            <ActiveOptionCard onClick={() => setConfirmOpen(true)}>
              <ActiveOptionIconWrap><ActiveOptionEmoji>🔊</ActiveOptionEmoji></ActiveOptionIconWrap>
              <ActiveOptionLabel>{t("voice_cloning.default_voice")}</ActiveOptionLabel>
              <ActiveOptionSub>{t("voice_cloning.brand_voice")}</ActiveOptionSub>
            </ActiveOptionCard>
          </ActiveOptionCards>
          <GhostButton onClick={handleReRecord}>{t("voice_cloning.re_record")}</GhostButton>
          {onProceed && (
            <ContinueButton onClick={onProceed}>Continue</ContinueButton>
          )}
        </Container>

        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>{t("voice_cloning.switch_to_default_title")}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t("voice_cloning.switch_to_default_body")}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>{t("general.cancel")}</Button>
            <Button
              style={{ color: "rgba(239, 68, 68, 1)" }}
              onClick={() => { setConfirmOpen(false); handleDelete(); }}
              autoFocus
            >
              {t("general.confirm")}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  if (showHeader) {
    return (
      <PageWrapper>
        <PageHeader>
          <PageTitle>{t("voice_cloning.onboarding_title")}</PageTitle>
        </PageHeader>
        <ContentArea>
          {renderContent()}
        </ContentArea>
      </PageWrapper>
    );
  }

  return renderContent();
};

export default VoiceCloningSettings;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px;
  width: 100%;
  max-width: 480px;
`;

const GhostButton = styled.button`
  background: none;
  border: none;
  color: #F59E0B;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  padding: 4px 0;
`;

const ContinueButton = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 14px;
  border: none;
  background: #F59E0B;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  margin-top: 8px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const ActiveBadge = styled.div`
  padding: 3px 10px;
  border-radius: 20px;
  background: rgba(72, 199, 142, 0.15);
  color: rgba(72, 199, 142, 1);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ActiveOptionCards = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const ActiveOptionCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 16px 12px;
  border-radius: 12px;
  cursor: ${({ selected }) => (selected ? "default" : "pointer")};
  transition: background 150ms ease, border-color 150ms ease;
  border: 1.5px solid
    ${({ selected }) =>
      selected ? "rgba(245, 158, 11, 0.35)" : "transparent"};
  background: ${({ theme, selected }) =>
    selected ? "rgba(245, 158, 11, 0.08)" : theme.menu.groupContentBackground};
  user-select: none;
`;

const ActiveOptionIconWrap = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ActiveOptionEmoji = styled.span`
  font-size: 28px;
  line-height: 1;
`;

const ActiveOptionLabel = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme, selected }) =>
    selected ? "#F59E0B" : theme.menu.primaryTextColor};
`;

const ActiveOptionSub = styled.p`
  font-size: 11px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 19px 16px;
  align-items: center;
  background: ${({ theme }) => theme.menu.background};
  border-bottom: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  flex-shrink: 0;
`;

const PageTitle = styled.h3`
  font-weight: 700;
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0.04px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: auto;
`;
